import { intersectionBy, cloneDeep, sortBy, groupBy } from 'lodash'
import { t } from 'plottr_locales'
import {
  ADD_LINE,
  ADD_LINE_WITH_TITLE,
  ADD_LINES_FROM_TEMPLATE,
  EDIT_LINE_TITLE,
  EXPAND_LINE,
  COLLAPSE_LINE,
  EDIT_LINE,
  EXPAND_TIMELINE,
  COLLAPSE_TIMELINE,
  CLEAR_TEMPLATE_FROM_TIMELINE,
  RESET_TIMELINE,
  EDIT_LINE_COLOR,
  REORDER_LINES,
  DELETE_LINE,
  FILE_LOADED,
  NEW_FILE,
  DELETE_BOOK,
  LOAD_LINES,
  ADD_BOOK_FROM_TEMPLATE,
  ADD_BOOK,
  DUPLICATE_LINE,
  MOVE_LINE,
  PIN_PLOTLINE,
  UNPIN_PLOTLINE,
  DUPLICATE_BOOK,
  REPLACE_MARKED_HITS,
  ADD_BOOK_FROM_PLTR,
  ACTION_CONFLICT_CHANGES,
  UNDO,
  REDO,
  UNDO_N_TIMES,
  REDO_N_TIMES,
} from '../constants/ActionTypes'
import { line } from '../store/initialState'
import { newFileLines, newFileSeriesLines } from '../store/newFileState'
import { nextId } from '../store/newIds'
import { nextColor } from '../store/lineColors'
import { nextPositionInBook, positionReset } from '../helpers/lists'
import { associateWithBroadestScope, isNotSeries, sortLinesWithPinnedFirst } from '../helpers/lines'
import { sortByHitPosition } from './sortByHitPosition'
import { safeParseInt } from './safeParseInt'
import { replacePlainTextHit, replaceInSlateDatastructure } from './replace'
import {
  extractLocalOnlyBookConflictDataArrayOfType,
  extractLocalAndOnlineBookConflictDataArrayOfType,
} from './extractConflictDataArrayOfType'
import { addConflictedEntities, emptyMissingEntities } from './addConflictedEntities'

/**
 * @typedef {import('../index').Line} Line
 * @typedef {import('../index').PltrAction} PltrAction
 * @typedef {Array<Line>} LinesState
 */

/**
 * @template A
 * @typedef {import('../types/reducer').DataRepairer<A>} DataRepairer
 */

// bookId is:
// Union of:
//  - bookId: Number,
//  - "series": String literal,

/**
 * @param {Set<Number>} numbersTaken
 * @param {LinesState} toReset
 * @param {String} positionName
 * @return {LinesState}
 */
export const positionResetWithAvailableNumbers = (numbersTaken, toReset, positionName) => {
  const resetted = cloneDeep(toReset)
  const numbersAssigned = new Set()
  for (let i = 0; i < toReset.length; ++i) {
    let index = i
    while (numbersTaken.has(index) || numbersAssigned.has(index)) {
      index++
    }
    resetted[i][positionName] = index
    numbersAssigned.add(index)
  }
  return resetted
}

/**
 * @param {LinesState} bookLines
 * @return {LinesState}
 */
export const positionResetBooksLines = (bookLines) => {
  const pinnedLines = bookLines
    .filter(({ isPinned }) => isPinned)
    .map((l, idx) => {
      return {
        ...l,
        pinnedPosition: l.pinnedPosition ?? idx,
      }
    })
  const unpinnedLines = bookLines.filter(({ isPinned }) => !isPinned)
  const positionsInPinnedLines = new Set(pinnedLines.map(({ position }) => position))
  return [
    ...positionReset(pinnedLines, 'pinnedPosition'),
    ...positionResetWithAvailableNumbers(positionsInPinnedLines, unpinnedLines, 'position'),
  ]
}

/**
 * @param {LinesState} allLines
 * @return {LinesState}
 */
export const positionResetEveryBook = (allLines) => {
  return Object.values(groupBy(allLines, ({ bookId }) => bookId)).flatMap((booksLines) => {
    return positionResetBooksLines(booksLines)
  })
}

/**
 * @param {LinesState} allLines
 * @return {LinesState}
 */
export const sortEveryBooksLines = (allLines) => {
  return Object.values(groupBy(allLines, ({ bookId }) => bookId)).flatMap((booksLines) => {
    return sortLinesWithPinnedFirst(booksLines)
  })
}

/**
 * @param {LinesState} state
 * @param {number} lineId
 * @param {number | 'series'} bookId
 * @param {boolean} isPinned
 * @param {boolean} expanded
 * @return {LinesState}
 */
export const pinUnpinPlotline = (state, lineId, bookId, isPinned, expanded) => {
  const originalBookLines = state.filter((l) => {
    return l.bookId == bookId
  })
  const pinnedCountInBook = originalBookLines.filter((l) => l.isPinned).length
  const bookLines = originalBookLines.map((l) => {
    if (l.id === lineId) {
      return {
        ...l,
        isPinned,
        expanded,
        ...(isPinned ? { pinnedPosition: pinnedCountInBook } : { pinnedPosition: null }),
      }
    } else {
      return l
    }
  })
  const pinnedLines = bookLines
    .filter((l) => l.isPinned)
    .map((l, idx) => {
      return {
        ...l,
        pinnedPosition: l.pinnedPosition ?? idx,
      }
    })
  const unpinnedLines = bookLines.filter((l) => !l.isPinned)
  const positionsTaken = new Set(pinnedLines.map(({ position }) => position))
  return [
    ...state.filter((l) => l && l.bookId != bookId),
    ...positionReset(sortBy(pinnedLines, 'pinnedPosition'), 'pinnedPosition'),
    ...positionResetWithAvailableNumbers(
      positionsTaken,
      sortBy(unpinnedLines, 'position'),
      'position'
    ),
  ]
}

/**
 * @param {DataRepairer<any>} _dataRepairers
 * @return {function(Array<Line>, PltrAction): Array<Line>}
 */
const lines = (_dataRepairers) => (state, action) => {
  const actionBookId = associateWithBroadestScope(
    ('bookId' in action && action.bookId) ||
      ('newBookId' in action && action.newBookId) ||
      undefined
  )

  switch (action.type) {
    case ADD_LINE: {
      const linesInBook = state.filter((l) => l.bookId == actionBookId)
      const colorsInBook = linesInBook.map((l) => {
        return l.color
      })
      return [
        {
          ...line,
          id: nextId(state),
          bookId: actionBookId,
          title: '',
          color: nextColor(linesInBook.length, colorsInBook),
          position: nextPositionInBook(state, actionBookId === 'all' ? 'series' : actionBookId),
          expanded: null,
          fromTemplateId: null,
        },
        ...state,
      ]
    }

    case ADD_BOOK:
    case ADD_LINE_WITH_TITLE: {
      const linesInBook_ = state.filter((l) => l.bookId == actionBookId).length
      return [
        {
          ...line,
          id: nextId(state),
          bookId: actionBookId,
          title: (action.type !== ADD_BOOK && action.title) || t('Main Plot'),
          color: nextColor(linesInBook_),
          position: nextPositionInBook(state, actionBookId === 'all' ? 'series' : actionBookId),
        },
        ...state,
      ]
    }

    case ADD_LINES_FROM_TEMPLATE:
    case ADD_BOOK_FROM_TEMPLATE: {
      if (Array.isArray(action.templateData?.lines)) {
        const linesInBook = state.filter((l) => l.bookId == actionBookId)
        const nextPosition = nextPositionInBook(
          linesInBook,
          actionBookId === 'all' ? 'series' : actionBookId
        )
        const newLines = sortBy(
          action.templateData.lines
            // this is to protect against a bad template that unnecessarily had a series line
            .filter(({ bookId }) => bookId !== 'series'),
          'position'
        ).map((l, index) => {
          const newLine = cloneDeep(l)
          newLine.id = action.nextLineId + newLine.id // give it a new id
          if (actionBookId !== 'all') {
            newLine.bookId = actionBookId // add it to the new/current book
          }
          newLine.position = nextPosition + newLine.position // put it in the right position
          newLine.fromTemplateId = action.id
          if (!newLine.color) {
            newLine.color = nextColor(linesInBook.length + index)
          }
          return newLine
        })
        return positionResetEveryBook(sortEveryBooksLines([...state, ...newLines]))
      } else {
        return state
      }
    }

    case ADD_BOOK_FROM_PLTR:
    case DUPLICATE_BOOK: {
      const newLines = action.newLines
        .filter(({ bookId }) => bookId !== 'series') // this is to protect against a bad template that unnecessarily had a series line
        .map((l, _index) => {
          const newLine = cloneDeep(l)
          newLine.id = action.nextLineId + newLine.id // give it a new id
          newLine.bookId = action.newBookId // add it to the new/current book
          return newLine
        })

      if (intersectionBy(state, newLines, 'id').length > 0) {
        return state
      } else {
        return [...state, ...newLines]
      }
    }

    case EDIT_LINE:
      return state.map((l) =>
        l.id === action.id ? Object.assign({}, l, { title: action.title, color: action.color }) : l
      )

    case EDIT_LINE_TITLE:
      return state.map((l) =>
        l.id === action.id ? Object.assign({}, l, { title: action.title }) : l
      )

    case EDIT_LINE_COLOR:
      return state.map((l) =>
        l.id === action.id ? Object.assign({}, l, { color: action.color }) : l
      )

    case DELETE_BOOK:
      return state.filter(({ bookId }) => bookId !== action.id)

    case DELETE_LINE:
      return state.filter((l) => l.id !== action.id)

    case PIN_PLOTLINE:
    case UNPIN_PLOTLINE: {
      if (actionBookId !== 'all') {
        return pinUnpinPlotline(
          state,
          action.lineId,
          actionBookId,
          action.type === PIN_PLOTLINE,
          false
        )
      } else {
        return state
      }
    }

    case REORDER_LINES:
      return [
        ...state.filter((l) => l && l.bookId != actionBookId),
        ...positionResetBooksLines(action.lines),
      ]

    case EXPAND_LINE:
      return state.map((l) =>
        l.id === action.id && !l?.isPinned ? Object.assign({}, l, { expanded: true }) : l
      )

    case COLLAPSE_LINE:
      return state.map((l) => (l.id === action.id ? Object.assign({}, l, { expanded: false }) : l))

    case COLLAPSE_TIMELINE:
    case EXPAND_TIMELINE:
      return state.map((l) => (!l?.isPinned ? Object.assign({}, l, { expanded: null }) : l))

    case CLEAR_TEMPLATE_FROM_TIMELINE: {
      /**
       * @type {{ book: Array<Line>, notBook: Array<Line> }}
       */
      const initialAcc = { book: [], notBook: [] }
      const values = state.reduce((acc, l) => {
        if (l.bookId == actionBookId) {
          if (l.fromTemplateId != action.templateId) acc.book.push(l)
        } else {
          acc.notBook.push(l)
        }
        return acc
      }, initialAcc)
      const bookLines = positionResetBooksLines(sortBy(values.book, 'position'))
      return values.notBook.concat(bookLines)
    }

    case RESET_TIMELINE: {
      // remove any from this book
      const linesToKeep = action.isSeries
        ? state.filter(isNotSeries)
        : state.filter((l) => l.bookId != actionBookId)
      // create a new line in the book so there's 1
      return [
        {
          ...line,
          id: nextId(state),
          bookId: actionBookId,
          title: t('Main Plot'),
          color: nextColor(0),
          position: 0,
          expanded: null,
          fromTemplateId: null,
        },
        ...linesToKeep,
      ]
    }

    case DUPLICATE_LINE: {
      const lineToDuplicate = state.find(({ id }) => id === action.id)
      // Couldn't find the requested line
      if (!lineToDuplicate) {
        return state
      }
      const duplicatedLine = {
        ...cloneDeep(lineToDuplicate),
        id: action.newLineId,
        position: action.position,
      }
      const linesInBook = state.filter(({ bookId }) => bookId === lineToDuplicate.bookId)
      const linesInBookWithUpdatedPositions = linesInBook.map((l) => {
        if (l.position < action.position) {
          return l
        } else {
          return {
            ...l,
            position: l.position + 1,
          }
        }
      })
      const linesNotInBook = state.filter(({ bookId }) => bookId !== lineToDuplicate.bookId)
      return [...linesInBookWithUpdatedPositions, ...linesNotInBook, duplicatedLine]
    }

    case MOVE_LINE: {
      return state.map((l) => {
        if (l.id === action.id) {
          return {
            ...l,
            bookId: action.destinationBookId,
          }
        }
        return l
      })
    }

    case REPLACE_MARKED_HITS: {
      const applicableHits = action.hitsMarkedForReplacement.filter((hit) => {
        // Only support replacing line titles for now.
        return hit.path.match(/^\/lines\/[0-9a-zA-Z]+\/title\/[0-9]+/)
      })
      // IMPORTANT!!!
      //
      // We sort by the hit position so that we deal with later hits
      // first.  By doing so, we don't invalidate the start position
      // of other hits when we replace those hits.
      //
      // i.e. it's fine to do multiple replacements in the same
      // field, as long as you replace the hits in reverse order,
      // i.e. the last hit first and the first hit last.
      return sortByHitPosition(applicableHits).reduce((acc, nextHit) => {
        const { path, hit } = nextHit
        const [_, _lines, rawLineId, type, ...rest] = path.split('/')
        const lineId = safeParseInt(rawLineId)
        return acc.map((nextLine) => {
          if (nextLine.id === lineId) {
            const [rawFocusStart] = rest
            const attributeName = type
            const attributeValue = nextLine[attributeName]
            const focusStart = safeParseInt(rawFocusStart)
            const replaceFunction = Array.isArray(attributeValue)
              ? replaceInSlateDatastructure
              : replacePlainTextHit
            return {
              ...nextLine,
              [attributeName]: replaceFunction(
                attributeValue,
                focusStart,
                hit,
                action.replacementText
              ),
            }
          } else {
            return nextLine
          }
        })
      }, state)
    }

    case ACTION_CONFLICT_CHANGES: {
      return addConflictedEntities(
        action.onlineFile.lines,
        'lines',
        'books',
        extractLocalOnlyBookConflictDataArrayOfType('lines', action.localOnly),
        extractLocalAndOnlineBookConflictDataArrayOfType('lines', action.changedLocalAndOnline),
        emptyMissingEntities,
        // There are no attributes, nor are there categories for
        // books.
        {},
        {},
        {},
        action.idMapping,
        action.mergedAttributes
      )
    }

    case UNDO_N_TIMES:
    case REDO_N_TIMES:
    case UNDO:
    case REDO: {
      if (Array.isArray(action.state.lines)) {
        return action.state.lines
      } else {
        return state
      }
    }

    case FILE_LOADED:
      return action.data.lines

    case NEW_FILE:
      return newFileLines

    case LOAD_LINES:
      return action.lines

    default:
      return state || [...newFileLines(), ...newFileSeriesLines()]
  }
}

export default lines
